import {useEffect,useState,useSyncExternalStore} from 'react';
import type {CarrierOtaPort,CarrierOtaSnapshot} from '../carrier-ota-port.ts';
import type {AndroidRuntimeIdentity} from '../smt-device-gate-bootstrap-consumer.ts';

type ConfirmAction='activate'|'rollback'|null;

const phaseCopy:Record<CarrierOtaSnapshot['phase'],{title:string;detail:string}>= {
  unavailable:{title:'此裝置未提供 OTA 控制',detail:'目前 Web UI 未連接 A 線 Native OTA Bridge。'},
  idle:{title:'等待檢查',detail:'只會喺你撳「檢查更新」之後查詢版本。'},
  checking:{title:'正在檢查更新…',detail:'只讀取版本 metadata；唔會自動下載。'},
  available:{title:'發現新版本',detail:'新版本只會喺你撳「下載更新」之後下載及驗證。'},
  downloading:{title:'正在下載並驗證…',detail:'A 線 Carrier 正在下載、驗簽同 Stage；目前版本繼續可用。'},
  staged:{title:'已下載，等待你安裝',detail:'Candidate 已由 A 線驗證完成；安裝／啟用仍然要你再次確認。'},
  activating:{title:'正在啟用…',detail:'A 線 Carrier 已收到明確啟用要求，SMT 可能重新啟動。'},
  'rolling-back':{title:'正在回復上一版本…',detail:'A 線 Carrier 已收到明確回滾要求，SMT 可能重新啟動。'},
  'up-to-date':{title:'已是最新版本',detail:'A 線回覆目前冇需要下載嘅新 Runtime。'},
  failed:{title:'操作失敗',detail:'A 線拒絕或未能完成操作；未有任何成功狀態會由 B 線自行補估。'},
};

const useOtaSnapshot=(port:CarrierOtaPort)=>useSyncExternalStore(port.subscribe,port.getSnapshot,port.getSnapshot);

export function OtaControlSheet({
  open,
  port,
  runtimeIdentity,
  onClose,
}:{
  open:boolean;
  port:CarrierOtaPort;
  runtimeIdentity:AndroidRuntimeIdentity|null;
  onClose:()=>void;
}){
  const snapshot=useOtaSnapshot(port);
  const [confirmAction,setConfirmAction]=useState<ConfirmAction>(null);
  const copy=phaseCopy[snapshot.phase];
  const busy=Boolean(snapshot.pendingAction)||snapshot.phase==='activating'||snapshot.phase==='rolling-back';
  const canDownload=snapshot.bridgeAvailable&&!busy&&snapshot.phase==='available';
  const canActivate=snapshot.bridgeAvailable&&!busy&&snapshot.phase==='staged';
  const canCheck=snapshot.bridgeAvailable&&!busy&&snapshot.phase!=='downloading';
  const canRollback=snapshot.bridgeAvailable&&!busy;

  useEffect(()=>{if(!open)setConfirmAction(null)},[open]);
  if(!open)return null;

  const close=()=>{if(!busy){setConfirmAction(null);onClose()}};
  const confirm=()=>{
    if(confirmAction==='activate')port.activate();
    if(confirmAction==='rollback')port.rollback();
    setConfirmAction(null);
  };

  return <div className="ota-sheet-backdrop" data-testid="ota-sheet-backdrop">
    <section className="ota-task-sheet" role="dialog" aria-modal="true" aria-labelledby="ota-sheet-title">
      <div className="ota-sheet-grabber" aria-hidden="true"/>
      <header className="ota-sheet-header">
        <div><span className="eyebrow">A LINE · RUNTIME OTA</span><h2 id="ota-sheet-title">系統更新</h2><p>控制中心只提供入口；下載、驗證、啟用同回滾全部仍由 A 線 Carrier 執行。</p></div>
        <button type="button" className="ota-sheet-close" aria-label="關閉系統更新" disabled={busy} onClick={close}>×</button>
      </header>

      <div className={`ota-status-card phase-${snapshot.phase}`}>
        <div className="ota-status-icon" aria-hidden="true">↻</div>
        <div><small>更新狀態</small><strong>{copy.title}</strong><p>{copy.detail}</p></div>
      </div>

      <div className="ota-version-grid">
        <article><small>目前 Runtime</small><strong>{runtimeIdentity?.runtimeVersion??'Packaged baseline / 未提供 ID'}</strong><span>{runtimeIdentity?.runtimeChannel??'stable'}</span></article>
        <article><small>可用／已 Stage Runtime</small><strong>{snapshot.latestUpdate?.runtimeVersion??'—'}</strong><span>{snapshot.latestUpdate?.channel??'等待 A 線回覆'}</span></article>
      </div>

      {snapshot.latestUpdate?<dl className="ota-update-meta">
        <div><dt>Release ID</dt><dd>{snapshot.latestUpdate.releaseId}</dd></div>
        <div><dt>最低 Carrier</dt><dd>vc{snapshot.latestUpdate.minCarrierVersionCode}</dd></div>
        <div><dt>Bridge</dt><dd>v{snapshot.latestUpdate.bridgeVersion}</dd></div>
      </dl>:null}

      {snapshot.errorCode?<div className="ota-error" role="alert"><b>錯誤</b><code>{snapshot.errorCode}</code><span>需要重新明確操作；B 線唔會當成成功。</span></div>:null}

      <div className="ota-manual-chain" aria-label="手動更新流程">
        <button type="button" aria-label="檢查更新" className="ota-step" disabled={!canCheck} onClick={()=>{setConfirmAction(null);port.check()}}><span aria-hidden="true">1</span><div><b>檢查更新</b><small>只檢查 metadata</small></div></button>
        <button type="button" aria-label="下載更新" className="ota-step" disabled={!canDownload} onClick={()=>{setConfirmAction(null);port.download()}}><span aria-hidden="true">2</span><div><b>下載更新</b><small>下載＋驗證＋Stage</small></div></button>
        <button type="button" aria-label="安裝／啟用" className="ota-step primary" disabled={!canActivate} onClick={()=>setConfirmAction('activate')}><span aria-hidden="true">3</span><div><b>安裝／啟用</b><small>必須再次確認</small></div></button>
      </div>

      <div className="ota-rollback-row">
        <div><b>需要回復？</b><span>只會要求 A 線回復上一個可信 Runtime。</span></div>
        <button type="button" disabled={!canRollback} onClick={()=>setConfirmAction('rollback')}>回復上一版本</button>
      </div>

      {confirmAction?<div className={`ota-confirm-card ${confirmAction==='rollback'?'danger':''}`} role="alertdialog" aria-label={confirmAction==='activate'?'確認安裝':'確認回復'}>
        <div><b>{confirmAction==='activate'?'確認安裝呢個已 Stage 版本？':'確認回復上一個可信版本？'}</b><p>{confirmAction==='activate'?'確認後 A 線會啟用 Candidate，SMT 可能重新啟動。':'確認後 A 線會執行 rollback，SMT 可能重新啟動。'}</p></div>
        <div className="ota-confirm-actions"><button type="button" onClick={()=>setConfirmAction(null)}>取消</button><button type="button" className="confirm" onClick={confirm}>{confirmAction==='activate'?'確認安裝並重新啟動 SMT':'確認回復並重新啟動 SMT'}</button></div>
      </div>:null}

      <footer><span>自動檢測可以由 A 線既有 Carrier 執行；B 線唔會自動下載或安裝。</span><b>手動控制</b></footer>
    </section>
  </div>;
}
